/**
 * Modal de **detalle de un slot** (`GET /api/v1/{orders|laser_orders}_for_slot`).
 *
 * Réplica del modal de órdenes de un slot del SSR (`schedule.html`,
 * `showOrdersForSlot`): la lista de órdenes de una fecha/hora con su estado, cliente,
 * atendido, estado de pago, monto y descripción. Controlado por `slot` del padre
 * (`null` = cerrado); los datos llegan del hook `useSlotOrders` (TanStack Query), sin
 * `fetch`. Estados de carga/error contemplados (no panel en blanco).
 *
 * Es **read-only**: la API de scheduling solo expone la lectura del slot. Las
 * acciones de completar/entregar del modal SSR pertenecían al dominio de Órdenes (ya
 * estrangulado, gestionado por `features/orders` sobre `/api/v1/orders`); aquí no se
 * replican para no acoplar el horario a transiciones de otro dominio.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

import {
  formatSaleAmount,
  paymentStatusLabel,
  schedulingErrorMessage,
  slotStatusLabel,
} from "../format";
import { useSlotOrders } from "../hooks/useScheduling";

/** Slot abierto (fecha ISO + hora) o `null` cuando el modal está cerrado. */
export interface OpenSlot {
  date: string;
  time: string;
}

interface SlotOrdersDialogProps {
  slot: OpenSlot | null;
  /** Modo láser: elige el endpoint y ajusta el título. */
  laser: boolean;
  /**
   * Si se muestra el monto de venta de cada orden. Réplica del SSR, que solo lo
   * mostraba a admin/supervisor; el padre (que conoce el rol) lo decide.
   */
  showAmount: boolean;
  onClose: () => void;
}

export function SlotOrdersDialog({ slot, laser, showAmount, onClose }: SlotOrdersDialogProps) {
  const query = useSlotOrders(slot?.date ?? null, slot?.time ?? null, laser);
  const title = laser ? "Órdenes láser" : "Órdenes";

  return (
    <Dialog
      open={slot !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {slot ? `${slot.date} · ${slot.time}` : ""}
        </DialogDescription>

        <div className="mt-4 max-h-[70vh] overflow-y-auto">
          {query.isPending ? (
            <p className="py-8 text-center text-sm text-slate-500" role="status">
              Cargando órdenes…
            </p>
          ) : query.isError ? (
            <p className="py-8 text-center text-sm text-red-600" role="alert">
              {schedulingErrorMessage(query.error)}
            </p>
          ) : query.data.orders.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              No hay órdenes en esta franja horaria.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {query.data.orders.map((order) => {
                const amount = formatSaleAmount(order.sale_amount);
                return (
                  <li
                    key={order.id ?? order.order_number}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">
                        Orden #{order.order_number}
                      </span>
                      <span className="inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {slotStatusLabel(order.status)}
                      </span>
                    </div>
                    <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-slate-600 sm:grid-cols-2">
                      <div className="flex gap-1">
                        <dt className="font-medium text-slate-700">Cliente:</dt>
                        <dd>{order.customer_name ?? "—"}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium text-slate-700">Atendido por:</dt>
                        <dd>{order.attendant ?? "No especificado"}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium text-slate-700">Estado de pago:</dt>
                        <dd>{paymentStatusLabel(order.payment_status)}</dd>
                      </div>
                      {showAmount && amount !== null ? (
                        <div className="flex gap-1">
                          <dt className="font-medium text-slate-700">Monto:</dt>
                          <dd>{amount}</dd>
                        </div>
                      ) : null}
                    </dl>
                    {order.description ? (
                      <p className="mt-2 text-sm text-slate-600">
                        <span className="font-medium text-slate-700">Descripción:</span>{" "}
                        {order.description}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
